import { useEffect, useState } from 'react';
import { ActionFooter, Button, MixerLoader, Modal, YesNo, YesNoModalWithReason } from '../../../components/Common';
import { EvaluationDiagram, EvaluationInfo, EvaluationTopicTable } from '../../../components/Evaluation';
import { EvaluationCalculate, EvaluationStatus, HolisticScore } from '../../../commonTypes/evaluation/calculate.types';
import { calculateHolisticScore, mapScoreLabelToValue, normalizeScore } from '../../../util/evaluation/calculate';
import { useHttpGetEvaluationOnMount, useHttpGetTopicsByCategoryNameOnMount } from '../../../hooks';
import { useParams, useNavigate } from 'react-router';
import { EvaluatorToEvaluateeRole } from '../../../commonTypes/evaluation/evaluator-impact.types';
import { mapStatus } from '../../../util/evaluation/mappers';
import useHttpGetEvaluationRoleTopicWeightOnMount from '../../../hooks/user/userApi/useHttpGetEvaluationRoleTopicWeightOnMount';
import { RootState } from '../../../store';
import { AuthState } from '../../../store/slices/auth.slice';
import { useSelector } from 'react-redux';
import useHttpRequestEvaluationUpdate from '../../../hooks/user/userApi/useHttpRequestEvaluationUpdate';
import { formatToMonthDayYear } from '../../../util/date/dateFormater';
import { useTranslation } from '../../../hooks/useTranslation';

const EvaluationPreview = () => {
    const { t } = useTranslation('evaluations');
    const { id } = useParams();
    const navigate = useNavigate();
    const [isUpdateRequested, setIsUpdateRequested] = useState<boolean>(false);
    const [updateReason, setUpdateReason] = useState<string>('');
    const { data, isReady } = useHttpGetEvaluationOnMount(id as string, isUpdateRequested);
    const topicIds = data?.topicScores.map(topicScore => topicScore.topicId);
    const roleId = data?.evaluatorToEvaluateeRoleId;
    const { data: evaluatorImpact } = useHttpGetEvaluationRoleTopicWeightOnMount(roleId, topicIds);
    const { data: devopsTopics } = useHttpGetTopicsByCategoryNameOnMount('DevOps');
    const { data: developerTopics } = useHttpGetTopicsByCategoryNameOnMount('Developer');
    const { data: culturalTopics } = useHttpGetTopicsByCategoryNameOnMount('Cultural');
    const { exec } = useHttpRequestEvaluationUpdate();
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const [chartData, setChartData] = useState<HolisticScore | null>(null);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const technicalTopics = [...(devopsTopics || []), ...(developerTopics || [])];

    const evaluationInfo = {
        evaluator: data?.isAnonymous
            ? 'Anonymous'
            : `${data?.evaluator?.profile?.givenName ?? '-'} ${data?.evaluator?.profile?.familyName ?? '-'}`.trim(),
        evaluatee: `${data?.evaluatee?.profile?.givenName ?? '-'} ${data?.evaluatee?.profile?.familyName ?? '-'}`.trim(),
        dueDate: data?.dueDate ? formatToMonthDayYear(data.dueDate.toString()) : '-',
        completionDate: data?.status === EvaluationStatus.SUBMITTED && data.updatedAt ? formatToMonthDayYear(data.updatedAt.toString()) : '-',
        status: data?.status ? mapStatus(data.status) : '-',
        isAnonymous: data?.isAnonymous ? 'Yes' : 'No',
    };
    const responses = data?.topicScores
        .filter(score => score.score)
        .map(score => {
            return {
                score: mapScoreLabelToValue(score.score),
                topic: score.topic.name,
                topicId: score.topic.id,
                note: score.note,
            };
        });

    useEffect(() => {
        if (!data && isReady) window.location.href = '/404';
    }, [data, isReady]);

    useEffect(() => {
        if (!data || !responses || !evaluatorImpact) return;

        const normalizedResponses = responses
            .filter(r => r.score !== undefined && r.score !== null)
            .map(r => ({
                ...r,
                score: normalizeScore(r.score),
                evaluator: data?.evaluator,
                topicId: r.topicId,
            }));

        const currentEvaluation: EvaluationCalculate = {
            evaluatorToEvaluateeRole: data.evaluatorToEvaluateeRole as {
                id: string;
                direction: EvaluatorToEvaluateeRole;
            },
            topicScores: normalizedResponses,
        };

        const holistic = calculateHolisticScore({
            combo: [currentEvaluation],
            evaluatorRoleTopicWeights: evaluatorImpact,
            technicalTopics,
            culturalTopics,
        });

        setChartData(holistic);
    }, [data, evaluatorImpact, devopsTopics, developerTopics, culturalTopics]);

    const todayAtMidnight = new Date();
    todayAtMidnight.setHours(0, 0, 0, 0);
    const isExpired = (dueDate: Date) => todayAtMidnight > new Date(dueDate);
    const canRequestUpdate = data?.status === EvaluationStatus.SUBMITTED && data.evaluatorUserSub === auth.value.userSub && !isExpired(data.dueDate);

    const button =
        data?.status === EvaluationStatus.UPDATE_REQUESTED || isUpdateRequested ? (
            <p>Update Requested</p>
        ) : (
            canRequestUpdate && <Button text="Request Update" variant="underline" onClick={() => setIsModalVisible(true)} />
        );

    return (
        <>
            {isReady ? (
                <>
                    <div className="evaluation-preview">
                        <div className="evaluation-preview__header">
                            <h1>Evaluation Results</h1>
                            {button}
                        </div>

                        <EvaluationInfo
                            evaluator={evaluationInfo.evaluator}
                            evaluatee={evaluationInfo.evaluatee}
                            dueDate={evaluationInfo.dueDate}
                            completionDate={evaluationInfo.completionDate}
                            status={evaluationInfo.status}
                            isAnonymous={evaluationInfo.isAnonymous}
                        />

                        <div className="evaluation-preview__results-grid">
                            <section className="evaluation-preview__section">
                                <h2>Topic Results</h2>
                                <EvaluationTopicTable tableInfo={responses} />
                            </section>

                            <section className="evaluation-preview__section evaluation-preview__section--diagram">
                                {responses && responses?.length > 0 && (
                                    <>
                                        <h2>Overall Results</h2>
                                        <EvaluationDiagram chartData={chartData} />
                                    </>
                                )}
                            </section>
                        </div>

                        {isModalVisible && (
                            <Modal
                                isVisible={isModalVisible}
                                closeModal={() => {
                                    setIsModalVisible(false);
                                }}
                            >
                                <YesNoModalWithReason
                                    placeholder={t('updateReasonPlaceholder')}
                                    updateReason={updateReason}
                                    setUpdateReason={setUpdateReason}
                                    title={t('updateModalTitle')}
                                    description={t('updateModalDescription')}
                                    onYes={() => {
                                        setIsModalVisible(false);
                                        exec(id as string, updateReason);
                                        setUpdateReason('');
                                        setIsUpdateRequested(true);
                                        if (data) data.status = EvaluationStatus.UPDATE_REQUESTED;
                                    }}
                                    onNo={() => {
                                        setIsModalVisible(false);
                                        setUpdateReason('');
                                    }}
                                    onYesText={t('updateModalAccept')}
                                    onNoText={t('updateModalReject')}
                                />
                            </Modal>
                        )}
                    </div>
                    <ActionFooter
                        buttons={[
                            {
                                text: 'Close',
                                onClick: () => navigate('/evaluation'),
                                variant: 'contained',
                            },
                        ]}
                        className="start-evaluation-process__footer"
                    />
                </>
            ) : (
                <MixerLoader position="centerOnParent" />
            )}
        </>
    );
};

export default EvaluationPreview;
